/**
 * 課題の変更履歴（項目1つにつき1行）を、保存操作1回につき1行の「誰が・いつ・何をしたか」にまとめる。
 *
 * 課題を作ると項目の数だけ行が届くので、操作ごとに束ねて「課題を作成」のように1行で見せる。値は文言にし（判定・優先度など）、
 * 自由文の項目は値を出さず「本文変更あり」とだけ出す。元に戻した操作は「取り消し:」を付ける。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題の変更履歴を保存操作ごとに束ね、誰が・いつ・何をしたか（判定の変更・声や案件の紐づけ・中身の変更）を1行で読めるようにする
 * @graph-connects formatIssueFieldValue [calls] 値の文言
 * @graph-connects ISSUE_FIELD_LABEL_KEYS [references] 項目名の文言
 */
import type { IssueChangeLogEntry } from '@cortex/domain-ac-mens-hub-api';

import { ISSUE_FIELD_LABEL_KEYS } from './issue-mgmt-format.js';
import { formatIssueFieldValue, type Translate } from './issue-mgmt-values.js';

/**
 * 変更履歴の1行（保存操作1回分）。
 * @graph-connects none
 */
export interface IssueHistoryLine {
  operationId: string;
  changedAt: string;
  actor: string;
  text: string;
}

/**
 * まとめるときに使うもの。
 * @graph-connects none
 */
export interface HistoryContext {
  t: Translate;
  nameOf: (email: string) => string;
  themeName: (themeId: string) => string;
}

/**
 * 履歴に出さない課題の項目（判定の項目と一緒に必ず変わる、判定日時と判定者）。
 * @graph-connects none
 */
const HIDDEN_ISSUE_FIELDS: ReadonlySet<string> = new Set(['decidedAt', 'decidedBy']);

/**
 * JSON の値を読む（null は null）。
 * @graph-connects none
 */
function parseValue(json: string | null): unknown {
  return json === null ? null : (JSON.parse(json) as unknown);
}

/**
 * 変えた人の表示。
 * @graph-connects none
 */
function actorLabel(entry: IssueChangeLogEntry, ctx: HistoryContext): string {
  switch (entry.actorType) {
    case 'user':
      return ctx.nameOf(entry.actor);
    case 'job':
      return ctx.t('issueMgmt.history.job', { name: entry.actor });
    case 'migration':
      return ctx.t('issueMgmt.history.migration');
    default: {
      const unreachable: never = entry.actorType;
      return String(unreachable);
    }
  }
}

/**
 * 変えたものの表示（課題・声・案件・テーマ・取込みチャンネル・点検）。
 * @graph-connects none
 */
function entityLabel(entry: IssueChangeLogEntry, ctx: HistoryContext): string {
  switch (entry.entityType) {
    case 'issue':
      return ctx.t('issueMgmt.history.entityIssue');
    case 'point':
      return ctx.t('issueMgmt.history.entityPoint', { id: entry.entityId.replace(/-\d+$/, '') });
    case 'voice':
      return ctx.t('issueMgmt.history.entityVoice', { id: entry.entityId });
    case 'case_link':
      return ctx.t('issueMgmt.history.entityCaseLink', {
        id: entry.entityId.slice(entry.entityId.indexOf('__') + 2),
      });
    case 'theme':
      return ctx.t('issueMgmt.history.entityTheme');
    case 'channel':
      return ctx.t('issueMgmt.history.entityChannel', { id: entry.entityId });
    case 'inspection':
      return ctx.t('issueMgmt.history.entityInspection', { id: entry.entityId });
    default: {
      const unreachable: never = entry.entityType;
      return String(unreachable);
    }
  }
}

/**
 * 元に戻した操作の、ドキュメントを作った・消したの別（行の前後の値の有無から読む）。
 * @graph-connects none
 */
function undoKind(rows: readonly IssueChangeLogEntry[]): 'create' | 'delete' | 'update' {
  const valued = rows.filter((r) => !r.valuesOmitted);
  if (valued.length > 1 && valued.every((r) => r.before === null && r.after !== null))
    return 'create';
  if (valued.length > 1 && valued.every((r) => r.before !== null && r.after === null))
    return 'delete';
  return 'update';
}

/**
 * 項目の変更1つの文。
 * @graph-connects formatIssueFieldValue [calls] 値の文言
 */
function changeText(
  entry: IssueChangeLogEntry,
  issueId: string,
  ctx: HistoryContext,
): string | null {
  if (entry.entityType === 'point') {
    if (entry.field !== 'issueId') return null;
    const entity = entityLabel(entry, ctx);
    if (parseValue(entry.after) === issueId) return ctx.t('issueMgmt.history.linked', { entity });
    return ctx.t('issueMgmt.history.unlinked', { entity });
  }
  if (entry.entityType === 'issue' && HIDDEN_ISSUE_FIELDS.has(entry.field)) return null;
  const labelKey = ISSUE_FIELD_LABEL_KEYS[entry.field];
  const field = labelKey === undefined ? entry.field : ctx.t(labelKey);
  if (entry.valuesOmitted) return ctx.t('issueMgmt.history.changedOmitted', { field });
  const show = (json: string | null) => {
    const value = parseValue(json);
    if (entry.field === 'themeIds' && Array.isArray(value)) {
      return formatIssueFieldValue(
        value.map((id) => ctx.themeName(String(id))),
        entry.field,
        ctx.t,
        ctx.nameOf,
      );
    }
    return formatIssueFieldValue(value, entry.field, ctx.t, ctx.nameOf);
  };
  return ctx.t('issueMgmt.history.changed', {
    field,
    before: show(entry.before),
    after: show(entry.after),
  });
}

/**
 * 先頭の行とその仲間の行（同じ保存操作・同じ対象）。
 * @graph-connects none
 */
interface RowGroup {
  first: IssueChangeLogEntry;
  rows: IssueChangeLogEntry[];
}

/**
 * 行を鍵ごとに束ねる（最初に現れた順を保つ）。
 * @graph-connects none
 */
function groupRows(
  rows: readonly IssueChangeLogEntry[],
  keyOf: (row: IssueChangeLogEntry) => string,
): RowGroup[] {
  const groups = new Map<string, RowGroup>();
  for (const row of rows) {
    const group = groups.get(keyOf(row));
    if (group === undefined) groups.set(keyOf(row), { first: row, rows: [row] });
    else group.rows.push(row);
  }
  return [...groups.values()];
}

/**
 * 保存操作1回分の行から、変えたもの1つ（同じ課題・声・案件）ごとの文を作る。
 * @graph-connects changeText [calls] 項目の変更
 */
function entityTexts({ first, rows }: RowGroup, issueId: string, ctx: HistoryContext): string[] {
  const kind =
    first.changeOperation === 'undo'
      ? undoKind(rows)
      : first.changeOperation === 'create' || first.changeOperation === 'delete'
        ? first.changeOperation
        : 'update';
  const entity = entityLabel(first, ctx);
  if (kind === 'create') {
    return [
      first.entityType === 'case_link'
        ? ctx.t('issueMgmt.history.linked', { entity })
        : ctx.t('issueMgmt.history.created', { entity }),
    ];
  }
  if (kind === 'delete') return [ctx.t('issueMgmt.history.unlinked', { entity })];
  return rows.flatMap((row) => {
    const text = changeText(row, issueId, ctx);
    return text === null ? [] : [text];
  });
}

/**
 * 変更履歴を保存操作ごとの行にまとめる（届いた順＝新しい順を保つ）。何も見せるものが無い操作は出さない。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題の変更履歴を保存操作ごとに束ね、誰が・いつ・何をしたかの1行にする。元に戻した操作は取り消しと分かるようにする
 * @graph-connects groupRows [calls] 保存操作・対象ごとに束ねる
 * @graph-connects entityTexts [calls] 変えたもの1つごとの文
 */
export function summarizeIssueHistory(
  entries: readonly IssueChangeLogEntry[],
  issueId: string,
  ctx: HistoryContext,
): IssueHistoryLine[] {
  return groupRows(entries, (row) => row.operationId).flatMap(({ first, rows }) => {
    const parts = groupRows(rows, (row) => `${row.entityType}/${row.entityId}`).flatMap((group) =>
      entityTexts(group, issueId, ctx),
    );
    if (parts.length === 0) return [];
    const text = parts.join('、');
    return [
      {
        operationId: first.operationId,
        changedAt: first.changedAt,
        actor: actorLabel(first, ctx),
        text: first.changeOperation === 'undo' ? ctx.t('issueMgmt.history.undo', { text }) : text,
      },
    ];
  });
}
